/**
 * Google OAuth callback landing for PFP.
 *
 * Emergent redirects the user back here with `#session_id=...` in the
 * URL fragment (never the query string, so it doesn't hit any server
 * logs). We POST that to `/api/auth/google/session`, receive our
 * regular JWT, mirror the localStorage layout the rest of the app
 * expects, then bounce the user to their correct home.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ParentAppAuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    // Emergent puts the session id in the fragment, NOT the query.
    const raw = window.location.hash || '';
    const params = new URLSearchParams(raw.startsWith('#') ? raw.slice(1) : raw);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setError('No session — please try signing in again.');
      setTimeout(() => navigate('/parent-app/login'), 2500);
      return;
    }

    (async () => {
      try {
        const r = await axios.post(
          `${BACKEND_URL}/api/auth/google/session`,
          { session_id: sessionId },
          { headers: { 'Content-Type': 'application/json' } },
        );
        const { token, user } = r.data || {};
        if (!token) throw new Error('no_token');
        // Match the shape the rest of the app expects.
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        // Smart-redirect based on capabilities. Fetch fresh /auth/me so
        // we get capabilities on the very first landing (login endpoint
        // response already includes them but /auth/me is authoritative
        // and cheap).
        const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const caps = me.data?.capabilities || {};
        localStorage.setItem('user', JSON.stringify(me.data));
        window.location.href =
          caps.pfp && caps.clinical_parent
            ? '/'
            : caps.pfp
              ? '/parent-app/home'
              : '/';
      } catch (e) {
        setError(
          e?.response?.data?.detail
            || "Couldn't complete Google sign-in. Try again or use email + password.",
        );
        setTimeout(() => navigate('/parent-app/login'), 3000);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="text-center" data-testid="parent-auth-callback">
        {error ? (
          <p className="text-red-600 text-sm max-w-sm">{error}</p>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin text-[#7C6F9C] mx-auto mb-3" />
            <p className="text-stone-600 text-sm">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
